/**
 * "Why this?" for one schedule block.
 *
 * The write verbs this surface needs already have a client:
 *   - confirm / skip-today / move  → services/aiSchedulerService.ts
 *   - PUT placement edits          → services/scheduleService.ts
 * A second client for those endpoints would be the same defect as two
 * engines answering one question, one layer down — so this module adds only
 * what neither carries: the full `why` object, including the parent item's
 * own declared facts.
 *
 * SC-3 (facts-not-wording): the server returns deterministic facts and the
 * surface phrases them. Nothing here is prose and nothing here is a judgement.
 * Single-block only: `why.parent` costs an extra store read, so this is
 * fetched on demand for the row the user asked about — never per row in a list
 * (`scheduleService.fetchBlockCommitment` is the cheap half when only the
 * commitment pair is needed).
 */

export interface BlockWhy {
  commitmentState: 'flexible' | 'confirmed' | null
  commitmentSource: string | null
  /**
   * False means the pair was DERIVED from the row's shape, not recorded as a
   * user act. Saying "you confirmed this" about an inference would be a lie,
   * so the surface must phrase an unstamped row differently.
   */
  commitmentStamped: boolean
  blockSource: string | null
  blockKind: string | null
  sourceItemType: string | null
  parent: {
    id: number
    title: string | null
    priority: string | null
    dueDate: string | null
    deadlineType: string | null
  } | null
}

/**
 * GET /api/schedule/events/<id>.
 *
 * Never throws: "we cannot explain this one" is a normal answer (a virtual
 * row, a deleted row, a 404 on an id that is not the user's), and the caller's
 * job is to say so rather than to fail.
 */
export async function fetchBlockWhy(blockId: number): Promise<BlockWhy | null> {
  try {
    const res = await fetch(`/api/schedule/events/${blockId}`, { credentials: 'include' })
    if (!res.ok) return null
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null
    if (!body || body.success === false) return null
    const why = (body.why ?? {}) as Record<string, unknown>
    const parent = (why.parent ?? null) as Record<string, unknown> | null
    return {
      commitmentState: (why.commitment_state as BlockWhy['commitmentState']) ?? null,
      commitmentSource: (why.commitment_source as string) ?? null,
      commitmentStamped: why.commitment_stamped === true,
      blockSource: (why.block_source as string) ?? null,
      blockKind: (why.block_kind as string) ?? null,
      sourceItemType: (why.source_item_type as string) ?? null,
      parent: parent
        ? {
          id: Number(parent.id),
          title: (parent.title as string) ?? null,
          priority: (parent.priority as string) ?? null,
          dueDate: (parent.due_date as string) ?? null,
          deadlineType: (parent.deadline_type as string) ?? null,
        }
        : null,
    }
  } catch {
    return null
  }
}
